<template>
  <!-- NOSONAR -->
  <div
    class="md:p-[16px] p-[8px] rounded-[16px] border-[1px] border-solid border-lightGrayBorder w-full"
    :data-test-id="`disinherit-card-${item?.id || 'unknown'}`"
    tabindex="0"
    @keydown.enter.prevent="triggerConfirmation"
    @keydown.space.prevent="triggerConfirmation"
  >
    <div class="flex gap-[12px] w-full">
      <div class="flex items-center">
        <img
          :src="image"
          class="h-[80px] w-[80px] md:h-[100px] md:w-[100px] object-cover rounded-full transition duration-300"
          :alt="`avatar-${name}`"
          :class="checked ? 'grayscale' : ''"
        />
      </div>
      <div class="flex-1">
        <div class="md:pb-[6px] pb-[3px]">
          <p
            class="font-just md:text-[16px] text-[11px] font-[600] text-darkBlue leading-[22px] tracking-[-0.7%] transition duration-300"
            :class="checked ? 'opacity-50' : ''"
          >
            {{ name }}
          </p>
        </div>
        <div class="border-[1px] border-solid border-whiteBlue"></div>
        <div class="relative">
          <div
            class="md:mt-[12px] mt-[5px] rounded-[16px] bg-greyLight/5 md:p-[8px] p-[2px] flex items-center justify-between gap-[18px]"
          >
            <div>
              <p
                class="font-just md:text-[16px] text-[11px] font-400 text-darkGray"
              >
                {{ displayActionLabel }}
              </p>
            </div>
            <div class="cursor-pointer">
              <BaseConfirmation
                v-model="checked"
                :items="confirmationItems"
                :header="header"
                width="md:w-[381px] w-[90%]"
                :switchId="`disinherit-switch-${item?.id || 'unknown'}`"
                ref="confirmationRef"
              >
                <template #default="{ close }">
                  <div class="px-[12px] py-[16px]">
                    <div>
                      <p
                        class="font-just font-500 text-[16px] leading-[100%] tracking-[0%]"
                      >
                        {{ checked ? displayYesInclude : displayConfirmation }}
                      </p>
                    </div>
                    <div class="pt-[16px] flex gap-[9px]">
                      <BaseButton
                        class="!bg-greyLight/10 !rounded-[4px] !text-softGray w-full !whitespace-nowrap"
                        :label="displayCancel"
                        @click="cancel(close)"
                      />
                      <BaseButton
                        class="!bg-alert/10 !rounded-[4px] !text-alert w-full !whitespace-nowrap"
                        :label="checked ? displayYesInclude : displayConfirm"
                        @click="confirm(close)"
                      />
                    </div>
                  </div>
                </template>
              </BaseConfirmation>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Status badge moved outside and positioned to span full width -->
    <div class="md:pt-[12px] pt-[10px]">
      <div
        class="border-[1px] border-solid !rounded-[6px]"
        :class="checked ? 'border-alert' : 'border-deepSky'"
      >
        <p
          class="font-just md:text-[12px] text-[10px] font-[600] py-[2px] ps-[8px] pe-[114px] whitespace-nowrap"
          :class="checked ? 'text-alert' : 'text-deepSky'"
        >
          {{ checked ? displayExcluded : displayIncluded }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["update:modelValue", "updateInherits"]);

const props = defineProps({
  image: String,
  name: String,
  header: String,
  actionLabel: {
    type: String,
    default: "Disinherit",
  },
  confirmationMessage: {
    type: String,
    default:
      "Are you sure you want to exclude this person from your will? This action may have legal and emotional consequences.",
  },
  includedText: {
    type: String,
    default: "Included in your will",
  },
  excludedText: {
    type: String,
    default: "Excluded from your will",
  },
  cancelLabel: {
    type: String,
    default: "No, Cancel",
  },
  confirmLabel: {
    type: String,
    default: "Yes, Exclude",
  },
  isDisinherited: {
    type: Boolean,
    default: false,
  },
  item: {},
});

const { t } = useI18n();
const checked = ref(props.isDisinherited);

watch(checked, (val) => emit("update:isDisinherited", val));

const displayActionLabel = computed(() => props.actionLabel === "Disinherit" ? t('wishesLabel.disinherit') : props.actionLabel);
const displayConfirmation = computed(() => props.confirmationMessage === "Are you sure you want to exclude this person from your will? This action may have legal and emotional consequences." ? t('wishesLabel.disinheritConfirmation') : props.confirmationMessage);
const displayIncluded = computed(() => props.includedText === "Included in your will" ? t('wishesLabel.includedInWill') : props.includedText);
const displayExcluded = computed(() => props.excludedText === "Excluded from your will" ? t('wishesLabel.excludedFromWill') : props.excludedText);
const displayCancel = computed(() => props.cancelLabel === "No, Cancel" ? t('commonLabels.noCancel') : props.cancelLabel);
const displayConfirm = computed(() => props.confirmLabel === "Yes, Exclude" ? t('commonLabels.yesDelete') : props.confirmLabel);
const displayYesInclude = computed(() => t('wishesLabel.yesInclude'));

const confirmationItems = computed(() => [
  {
    label: displayConfirmation.value,
  },
]);

const cancel = (close) => {
  // When canceling, keep the current state (don't change it)
  close();
};

const confirm = (close) => {
  // When confirming, toggle the state
  checked.value = !checked.value;
  emit("updateInherits", props.item, checked.value, close);
};

const confirmationRef = ref(null);

const triggerConfirmation = () => {
  // Find the interactive element within BaseConfirmation and trigger mousedown to open popover
  const switchEl = confirmationRef.value?.$el?.firstElementChild;
  if (switchEl) {
    const event = new MouseEvent("mousedown", {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    switchEl.dispatchEvent(event);
  }
};
</script>
